import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { titleForRoute, TITLE_PREFIX } from '@/lib/routing/documentTitle';
import { useOpenGameStore } from '@/stores/openGame';

/**
 * Keep the browser tab title in step with the route (#781).
 *
 * Mounted once on the authed shell — the layout every routed page renders
 * inside — so no view owns its own title and a new page inherits one for
 * free. The one thing a view contributes is the open game's resolved
 * identity, which it publishes to `openGame` rather than writing a title.
 *
 * The detached Recent Fills window is the exception: it lives above the shell
 * and sets/restores its own title, since it must hand the title back to
 * whatever window it borrowed.
 */
export function useDocumentTitle(): void {
  const { pathname } = useLocation();
  const game = useOpenGameStore((s) => s.game);

  useEffect(() => {
    document.title = titleForRoute(pathname, { game });
  }, [pathname, game]);

  // Logging out unmounts the shell onto /login, which has no title of its
  // own — leave the last section's name up there and the login page claims to
  // be the Position Tracker.
  useEffect(() => () => {
    document.title = TITLE_PREFIX;
  }, []);
}
